import { useGraphqlQuery } from "graphql-mock"
import { Loader } from "materia"
import type { ReactNode } from "react"
import { QueryBoundary } from "utility-components"
import { useIsFeatureFlagEnabled } from "../../tools/useIsFeatureFlagEnabled"
import { JourneyNavigation } from "../JourneyNavigation"
import { DiplomaWidgetConnected } from "./DiplomaWidget"
import { EventWidgetConnected } from "./EventWidget"
import { KpiSalesWidgetConnected } from "./KpiSalesWidget"
import { LeaderboardWidgetLoader } from "./LeaderboardWidget/LeaderboardWidgetLoader"
import { NewsWidgetConnected } from "./NewsWidget"
import { RecentActivityWidgetConnected } from "./RecentActivityWidget"
import { SubHeaders } from "./SubHeaders"
import { UserWidget } from "./UserWidget"
import { WidgetLayout } from "./WidgetLayout"
import { query } from "./gql"

type WidgetLayoutLoaderProps = {
  children: ReactNode
  showUserInfoHeader?: boolean
  showJourneyNavigation?: boolean
  showMandatoryAndIncompleteFilters?: boolean
}
export const WidgetLayoutLoader = ({
  children,
  showUserInfoHeader,
  showJourneyNavigation = false,
  showMandatoryAndIncompleteFilters = false,
}: WidgetLayoutLoaderProps) => (
  <>
    <SubHeaders showUserInfoHeader={showUserInfoHeader} />
    <WidgetLayout
      widgets={
        <WidgetsLoader
          showJourneyNavigation={showJourneyNavigation}
          showMandatoryAndIncompleteFilters={showMandatoryAndIncompleteFilters}
        />
      }
    >
      <QueryBoundary fallback={<Loader />}>{children}</QueryBoundary>
    </WidgetLayout>
  </>
)

type WidgetsLoaderProps = {
  showJourneyNavigation: boolean
  showMandatoryAndIncompleteFilters: boolean
}
const WidgetsLoader = ({
  showJourneyNavigation,
  showMandatoryAndIncompleteFilters,
}: WidgetsLoaderProps) => {
  const {
    isEventWidgetEnabled,
    isPortalNewsEnabled,
    isLeaderboardsEnabled,
    isActivityFeedEnabled,
    isKpiSalesWidgetEnabled,
    isFlagsLoaded,
  } = useFeatureFlags()

  const { data, isPending } = useGraphqlQuery(
    query,
    {},
    { enabled: showJourneyNavigation }
  )

  // If there is 2 journeys or more, we show JourneyNavigation.
  // Otherwise UserWidget will be shown.
  const hasMultipleJourneys =
    (data?.productCategories.pageInfo.totalItemCount ?? 0) > 1
  const shouldShowJourneysWidget =
    showJourneyNavigation && (hasMultipleJourneys || isPending)

  if (!isFlagsLoaded) return <WidgetLayout.WidgetsSkeleton />

  return (
    <>
      {shouldShowJourneysWidget ? (
        <JourneyNavigation />
      ) : (
        <UserWidget showFilters={showMandatoryAndIncompleteFilters} />
      )}
      <DiplomaWidgetConnected />
      {isEventWidgetEnabled && <EventWidgetConnected />}
      {isPortalNewsEnabled && <NewsWidgetConnected />}
      {isLeaderboardsEnabled && <LeaderboardWidgetLoader />}
      {isActivityFeedEnabled && <RecentActivityWidgetConnected />}
      {isKpiSalesWidgetEnabled && <KpiSalesWidgetConnected />}
    </>
  )
}

const useFeatureFlags = () => {
  const { getIsPortalFlagEnabled, isFlagsLoaded } = useIsFeatureFlagEnabled()

  const isActivityFeedEnabled = getIsPortalFlagEnabled("enable_activity_feed")
  const isEventWidgetEnabled = !getIsPortalFlagEnabled(
    "disable_event_widget_page"
  )
  const isKpiSalesWidgetEnabled = getIsPortalFlagEnabled(
    "enable_kpi_sales_tiles"
  )
  const isLeaderboardsEnabled = !getIsPortalFlagEnabled("disable_leaderboard")
  const isPortalNewsEnabled = getIsPortalFlagEnabled("enable_portal_news")

  return {
    isFlagsLoaded,
    isActivityFeedEnabled,
    isEventWidgetEnabled,
    isKpiSalesWidgetEnabled,
    isLeaderboardsEnabled,
    isPortalNewsEnabled,
  }
}
